<template>
    <div class="main-layout">
        <!-- 侧边栏 -->
        <aside class="sidebar" :class="{ collapsed: isCollapsed }">
            <div class="sidebar-header">
                <div class="logo">
                    <el-icon v-if="!isCollapsed" class="logo-icon">
                        <Document />
                    </el-icon>
                    <span v-if="!isCollapsed" class="logo-text">检验报告系统</span>
                </div>
                <el-button class="collapse-btn" link size="small" @click="isCollapsed = !isCollapsed">
                    <el-icon>
                        <Fold v-if="!isCollapsed" />
                        <Expand v-else />
                    </el-icon>
                </el-button>
            </div>

            <el-menu :default-active="currentRoute" class="sidebar-menu" :collapse="isCollapsed" router unique-opened>
                <el-menu-item index="/dashboard">
                    <el-icon>
                        <DataBoard />
                    </el-icon>
                    <template #title>仪表板</template>
                </el-menu-item>

                <el-sub-menu index="reports" v-if="canViewReports">
                    <template #title>
                        <el-icon>
                            <Document />
                        </el-icon>
                        <span>报告管理</span>
                    </template>
                    <el-menu-item index="/reports">报告列表</el-menu-item>
                    <el-menu-item index="/reports/upload" v-if="canUploadReports">上传报告</el-menu-item>
                </el-sub-menu>

                <el-menu-item index="/hospitals" v-if="canManageHospitals">
                    <el-icon>
                        <OfficeBuilding />
                    </el-icon>
                    <template #title>医院管理</template>
                </el-menu-item>

                <el-menu-item index="/logs" v-if="isAdmin">
                    <el-icon>
                        <List />
                    </el-icon>
                    <template #title>日志管理</template>
                </el-menu-item>

                <el-menu-item index="/users" v-if="isAdmin">
                    <el-icon>
                        <User />
                    </el-icon>
                    <template #title>用户管理</template>
                </el-menu-item>

                <el-menu-item index="/test-connection" v-if="isAdmin">
                    <el-icon>
                        <Connection />
                    </el-icon>
                    <template #title>连接测试</template>
                </el-menu-item>
                
                <el-menu-item index="/sync" v-if="canViewSync">
                    <el-icon>
                        <RefreshRight />
                    </el-icon>
                    <template #title>同步管理</template>
                </el-menu-item>
            </el-menu>
        </aside>

        <!-- 主要内容区域 -->
        <div class="main-content">
            <!-- 顶部导航栏 -->
            <header class="header">
                <div class="header-left">
                    <el-breadcrumb separator="/">
                        <el-breadcrumb-item v-for="item in breadcrumbs" :key="item.path" :to="item.path">
                            {{ item.title }}
                        </el-breadcrumb-item>
                    </el-breadcrumb>
                </div>

                <div class="header-right">
                    <el-dropdown trigger="click">
                        <div class="user-info">
                            <el-avatar :size="32" class="user-avatar">
                                {{ user?.username?.charAt(0)?.toUpperCase() }}
                            </el-avatar>
                            <span class="username">{{ user?.username }}</span>
                            <el-icon class="el-icon--right">
                                <arrow-down />
                            </el-icon>
                        </div>
                        <template #dropdown>
                            <el-dropdown-menu>
                                <el-dropdown-item @click="goToProfile">
                                    <el-icon>
                                        <User />
                                    </el-icon>
                                    个人资料
                                </el-dropdown-item>
                                <el-dropdown-item divided @click="handleLogout">
                                    <el-icon>
                                        <SwitchButton />
                                    </el-icon>
                                    退出登录
                                </el-dropdown-item>
                            </el-dropdown-menu>
                        </template>
                    </el-dropdown>
                </div>
            </header>

            <!-- 页面内容 -->
            <main class="content">
                <router-view />
            </main>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import { ElMessage, ElMessageBox } from "element-plus";
import {
    Document,
    DataBoard,
    OfficeBuilding,
    List,
    User,
    Fold,
    Expand,
    ArrowDown,
    SwitchButton,
    Connection,
    RefreshRight,
} from "@element-plus/icons-vue";
import { useAuthStore } from "@/stores/auth";

const route = useRoute();
const router = useRouter();
const authStore = useAuthStore();

const isCollapsed = ref(false);

const user = computed(() => authStore.user);
const isAdmin = computed(() => authStore.isAdmin);
const isLabUser = computed(() => authStore.isLabUser);

// 权限控制
const canViewReports = computed(() => authStore.isAdmin || authStore.isLabUser);
const canUploadReports = computed(() => authStore.isAdmin || authStore.isLabUser);
const canManageHospitals = computed(() => authStore.isAdmin);
const canViewSync = computed(() => authStore.isAdmin || authStore.isLabUser);

const currentRoute = computed(() => route.path);

// 面包屑导航
const breadcrumbs = computed(() => {
    const matched = route.matched.filter(item => item.meta?.title);
    return matched.map(item => ({
        path: item.path,
        title: item.meta?.title as string,
    }));
});

const goToProfile = () => {
    router.push("/profile");
};

const handleLogout = async () => {
    try {
        await ElMessageBox.confirm("确定要退出登录吗？", "提示", {
            confirmButtonText: "确定",
            cancelButtonText: "取消",
            type: "warning",
        });

        await authStore.logout();
        ElMessage.success("已成功退出登录");
    } catch (error) {
        // 用户取消退出
    }
};

onMounted(() => {
    // 检查认证状态
    authStore.checkAuth();
});
</script>

<style scoped>
.main-layout {
    display: flex;
    min-height: 100vh;
}

.sidebar {
    width: 200px;
    background: #304156;
    transition: width 0.3s;
    position: relative;
}

.sidebar.collapsed {
    width: 64px;
}

.sidebar-header {
    height: 60px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 16px;
    background: #2c3e50;
    border-bottom: 1px solid #34495e;
}

.logo {
    display: flex;
    align-items: center;
    color: white;
}

.logo-icon {
    font-size: 24px;
    margin-right: 8px;
    color: #409eff;
}

.logo-text {
    font-size: 16px;
    font-weight: 600;
}

.collapse-btn {
    color: white;
    border: none;
}

.collapse-btn:hover {
    background: rgba(255, 255, 255, 0.1);
}

.sidebar-menu {
    border: none;
    background: #304156;
}

.sidebar-menu:not(.el-menu--collapse) {
    width: 200px;
}

.sidebar-menu .el-menu-item,
.sidebar-menu .el-sub-menu__title {
    color: #bfcbd9 !important;
    background: transparent !important;
}

.sidebar-menu .el-menu-item:hover,
.sidebar-menu .el-sub-menu__title:hover {
    background: #263445 !important;
    color: #409eff !important;
}

.sidebar-menu .el-menu-item.is-active {
    background: #409eff !important;
    color: white !important;
}

/* 确保子菜单标题中的 span 也使用相同颜色 */
.sidebar-menu .el-sub-menu__title span {
    color: #bfcbd9 !important;
}

/* 子菜单hover时的span颜色 */
.sidebar-menu .el-sub-menu__title:hover span {
    color: #409eff !important;
}

/* 子菜单展开时的标题颜色和背景 */
.sidebar-menu .el-sub-menu.is-active > .el-sub-menu__title {
    color: #409eff !important;
    background: transparent !important;
}

.sidebar-menu .el-sub-menu.is-active > .el-sub-menu__title span {
    color: #409eff !important;
}

/* 子菜单内的菜单项 */
.sidebar-menu .el-menu-item {
    background: #1f2d3d !important;
}

.sidebar-menu .el-menu-item:hover {
    background: #263445 !important;
}

.main-content {
    flex: 1;
    display: flex;
    flex-direction: column;
}

.header {
    height: 60px;
    background: white;
    border-bottom: 1px solid #ebeef5;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 20px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.header-left {
    flex: 1;
}

.header-right {
    display: flex;
    align-items: center;
}

.user-info {
    display: flex;
    align-items: center;
    cursor: pointer;
    padding: 8px;
    border-radius: 4px;
    transition: background-color 0.3s;
}

.user-info:hover {
    background: #f5f7fa;
}

.user-avatar {
    margin-right: 8px;
    background: #409eff;
}

.username {
    margin-right: 8px;
    font-size: 14px;
    color: #303133;
}

.content {
    flex: 1;
    padding: 20px;
    background: #f0f2f5;
    overflow-y: auto;
}

/* 响应式设计 */
@media (max-width: 768px) {
    .sidebar {
        position: fixed;
        left: -200px;
        top: 0;
        height: 100%;
        z-index: 1000;
        transition: left 0.3s;
    }

    .sidebar.mobile-show {
        left: 0;
    }

    .sidebar.collapsed {
        left: -64px;
    }

    .header-left .el-breadcrumb {
        font-size: 12px;
    }

    .username {
        display: none;
    }
}
</style>